<template>
  <div class="content-text mt-3">
    <h2 class="title-2 text-left">3 Vínculo e acompanhamento</h2>

    <p>
      Esse componente incentiva a vinculação da população à sua equipe de
      referência e a realização do cuidado a essa população. Não basta somente
      cadastrar, é preciso entender quais são as pessoas do território que fazem
      uso dos serviços ofertados na Unidade Básica de Saúde (UBS), além de
      estimular a equipe a visitar as famílias e, dessa forma, fortalecer a
      Estratégia de Saúde da Família (ESF).
    </p>

    <p>
      Segundo o IBGE, o município de {{ dados.etapa1.cidade.no_municipio }} –
      {{ dados.etapa1.estado.uf }}, possui {{ populacao }} habitantes. O Quadro
      2 apresenta a quantidade mínima e máxima de cadastros por equipe, assim
      como a quantidade de pessoas no SISAB até dezembro de 2023. É fundamental
      equilibrar o número mínimo e máximo de pessoas por equipe. Para alcançar
      essa equidade, é possível redistribuir os indivíduos entre as equipes já
      existentes por meio de remapeamento e territorialização, ou até mesmo
      implantar novas equipes, caso o e-GESTOR apresente teto para credenciar
      mais equipes.
    </p>

    <div class="container-table">
      <span style="text-transform: uppercase; font-weight: bold"
        >Quadro 2 - Quantidade mínima e máxima de cadastros por equipe</span
      >
      <table>
        <thead>
          <tr>
            <th>UBS</th>
            <th>CNES</th>
            <th>INE</th>
            <th>TIPO</th>
            <th>CADASTROS MÍNIMOS</th>
            <th>CADASTROS SISAB</th>
            <th>CADASTROS MÁXIMO</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in ubs" :key="index">
            <td>{{ item.no_ubs }}</td>
            <td class="text-right">{{ item.cnes }}</td>
            <td class="text-right">{{ item.ine }}</td>
            <td>{{ item.tipo_equipe }}</td>
            <td class="text-right">{{ item.min_cad }}</td>
            <td class="text-right">{{ item.cad_sisab }}</td>
            <td class="text-right">{{ item.max_cad }}</td>
          </tr>
        </tbody>
      </table>
      <span class="fonte" v-html="fonte"></span>
    </div>

    <p class="mt-4">
      Vale salientar que o atendimento não pode ser negado na UBS. As pessoas
      beneficiárias do Programa Bolsa Família (PBF), Benefício de Prestação
      Continuada (BPC), os idosos com 60 anos ou mais e as crianças de até 5
      anos de idade são priorizadas no acompanhamento para esse componente.
    </p>

    <p>
      O Quadro 3 apresenta a quantidade por tipo de equipe, os valores por
      equipe e o total mensal a ser recebido neste componente.
    </p>

    <div class="container-table">
      <span style="text-transform: uppercase; font-weight: bold"
        >Quadro 3 - Valores por tipo de equipe componente vínculo e
        acompanhamento</span
      >
      <table>
        <thead>
          <tr>
            <th>EQUIPES</th>
            <th>QUANTIDADE</th>
            <th>CLASSIFICAÇÃO</th>
            <th>VALOR POR EQUIPE</th>
            <th>TOTAL POR EQUIPE</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in equipes.filter((el) => el.quantidade > 0)"
            :key="index"
          >
            <td>{{ item.label }}</td>
            <td class="text-right">{{ item.quantidade }}</td>
            <td>{{ item.classificacao }}</td>
            <td class="text-right">R$ {{ item.valorPorEquipe }}</td>
            <td class="text-right">R$ {{ item.valorTotalPorEquipe }}</td>
          </tr>
          <tr>
            <td colspan="4">Total geral</td>
            <td class="text-right">
              R$
              {{
                totalGeral.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                })
              }}
            </td>
          </tr>
        </tbody>
      </table>
      <span class="fonte" v-html="fonte"></span>
    </div>

    <p class="mt-3">
      Os indicadores do cofinanciamento federal da APS ainda não estão
      definidos. A classificação das equipes está dividida em
      <b>REGULAR, SUFICIENTE, BOM e ÓTIMO</b>. O ÓTIMO é o máximo que o
      município pode alcançar, somente após o período de transição (maio de 2024
      até abril de 2025). A implantação imediata do Monitoramento e Avaliação na
      APS (M & A na APS) torna mais fácil atingir o <b>"ÓTIMO"</b> como
      desempenho máximo municipal.
    </p>
  </div>
</template>

<script>
export default {
  props: {
    dados: {
      type: Object,
      default: () => {},
    },
    regras: {
      type: Array,
      default: () => {},
    },
    fonte: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      populacao: 0,
      ubs: [],
      equipes: [],
      totalGeral: 0,
    }
  },
  mounted() {
    this.buscarMunicipio()
    this.buscarUbsDoMunicipio()
    this.formatarEquipes()
  },
  computed: {},
  methods: {
    formatarEquipes() {
      const equipesImplantadas = this.dados.etapa4
      if (equipesImplantadas != null) {
        for (let label in equipesImplantadas) {
          const resultado = this.regras.find((item) => item.label === label)
          if (resultado) {
            let totalPorEquipe = 0
            const classificacaoEquipe = resultado.classificacao.find(
              (el) => el.tipo == 'Bom'
            )
            let quantidadeDeEquipes = equipesImplantadas[label]
            totalPorEquipe += classificacaoEquipe.valor * quantidadeDeEquipes
            this.equipes.push({
              label: resultado.label,
              quantidade: equipesImplantadas[label],
              classificacao: 'BOM',
              valorPorEquipe: classificacaoEquipe.valor.toLocaleString(
                'pt-BR',
                {
                  minimumFractionDigits: 2,
                }
              ),
              valorTotalPorEquipe: totalPorEquipe.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              }),
            })
            this.totalGeral += totalPorEquipe
          }
        }
      }
    },

    async buscarMunicipio() {
      try {
        const response = await this.$apiPublic.get(
          `/cofinanciamento-2024/populacao/${this.dados.etapa1.cidade.co_ibge}`
        )
        this.populacao = response.data.populacaoIBGE
      } catch (error) {
        console.log(error)
      }
    },

    async buscarUbsDoMunicipio() {
      try {
        const response = await this.$apiPublic.get(
          `/cofinanciamento-2024/ubs/${this.dados.etapa1.cidade.co_ibge}`
        )
        this.ubs = response.data
      } catch (error) {
        console.log(error)
      }
    },
  },
}
</script>

<style lang="scss"></style>
